import { type Cruz, lookup, Vehicle } from '@cruz-com/lookup'
import _ from 'lodash'
import moment from 'moment'
import Image from 'next/image'
import NextLink from 'next/link'
import { useCallback } from 'react'
import { CardBody, CardItem, Link, Separator } from '@/components'
import { DateTimeFormat } from '@/settings'

type Props = Vehicle.Summary & {
  showPostStatus?: boolean
  showSaleStatus?: boolean
  showSavedInfo?: boolean
  user?: Partial<Cruz.User>
}

export default function VehicleCard(d: Props) {
  const url = `/cruz/vehicle?id=${d._id}`
  const photoCount = _.size(d.photo_list)

  const photoUrl = d?.photo_preview_url

  const { showSavedInfo, user, showPostStatus, showSaleStatus } = d

  const handleImageError = useCallback((e: React.SyntheticEvent<HTMLImageElement>) => {
    e.currentTarget.src = '/img/noPhoto.svg'
  }, [])

  const getPostStatus = useCallback(() => {
    if (!d?.post_status_id) {
      return '-'
    }
    return _.find(lookup.other.postStatus, { id: d.post_status_id })?.name || '-'
  }, [d?.post_status_id])

  const getSaleStatus = useCallback(() => {
    if (!d?.sale_status_id) {
      return '-'
    }
    return _.find(lookup.vehicle.saleStatus, { id: d.sale_status_id })?.name || '-'
  }, [d?.sale_status_id])

  const Location = useCallback(() => {
    if (!_.isEmpty(d?.user_display)) {
      const { first_name, last_name, location } = d.user_display || {}
      return (
        <div className='px-4 py-3 border-t text-sm'>
          <div className='font-semibold'>
            {first_name} {last_name}
          </div>
          {_.isEmpty(location) ? (
            <div className='font-light text-xs'>---</div>
          ) : (
            <div className='font-light text-xs'>
              {location.city}, {location.state_abbr} {location.zip_code}
            </div>
          )}
        </div>
      )
    }
    return (
      <NextLink href={`/carsforsale-import/location?id=${d?.location?._id as string}`}>
        <div className='hover:bg-zinc-100 dark:hover:bg-zinc-700 px-4 py-3 border-t text-sm'>
          <div className='font-semibold'>{d?.location?.name}</div>
          <div className='font-light'>
            {d?.location?.city}, {d?.location?.state_abbr} {d?.location?.zip_code}
          </div>
        </div>
      </NextLink>
    )
  }, [d?.location?._id])

  const SavedInfo = () => {
    if (!showSavedInfo) {
      return null
    }
    const { user, date_saved } = d
    const mt = moment(date_saved)
    const { first_name, last_name, email, location } = user || {}
    return (
      <NextLink href={`/cruz/user?id=${d.user?._id}`}>
        <div className='hover:bg-zinc-100 dark:hover:bg-zinc-700 px-4 py-3 border-t text-sm'>
          {_.isEmpty(first_name) ? (
            <div className='font-semibold'>{email}</div>
          ) : (
            <div className='font-semibold'>
              {first_name} {last_name}
            </div>
          )}
          {_.isEmpty(location) ? (
            <div className='font-light'>---</div>
          ) : (
            <div className='font-light'>
              {location.city}, {location.state_abbr} {location.zip_code}
            </div>
          )}
          <div className='font-light text-sm mt-2'>
            <span className='font-medium'>Saved {mt.fromNow()}</span>
            <span className='ml-2'>({mt.format(DateTimeFormat)})</span>
          </div>
        </div>
      </NextLink>
    )
  }

  const UserInfo = () => {
    if (!user) {
      if (!d.date_last_view) {
        return null
      }
      return (
        <div className='border-t text-sm px-4 py-3 font-light'>
          Anonymous User
          <br />
          Viewed <span className='font-medium'>{moment(d.date_last_view).fromNow()}</span>
        </div>
      )
    }
    return (
      <NextLink href={`/cruz/user?id=${user._id}`}>
        <div className='hover:bg-zinc-100 dark:hover:bg-zinc-700 px-4 py-3 border-t text-sm'>
          {_.isEmpty(user.first_name) ? (
            <div className='font-semibold'>{user.email}</div>
          ) : (
            <div className='font-semibold'>
              {user.first_name} {user.last_name}
            </div>
          )}
          {_.isEmpty(user.location) ? (
            <div className='font-light'>---</div>
          ) : (
            <div className='font-light'>
              {user.location.city}, {user.location.state_abbr} {user.location.zip_code}
            </div>
          )}
          {d.date_last_view && (
            <div className='font-light mt-1'>
              Viewed <span className='font-medium'>{moment(d.date_last_view).fromNow()}</span>
            </div>
          )}
        </div>
      </NextLink>
    )
  }

  return (
    <>
      <div className='border-b relative'>
        {photoCount > 0 && (
          <div className='absolute bottom-3 left-0 text-xs/6 pl-2 pr-4 py-0.5 text-white bg-zinc-700/50 rounded-r-full'>
            {photoCount} photo{photoCount > 1 && 's'}
          </div>
        )}
        <Link href={url}>
          <Image
            width={480}
            height={480}
            src={photoUrl || '/img/noPhoto.svg'}
            alt={'Photo'}
            priority={true}
            fetchPriority='high'
            unoptimized={true}
            className={_.isEmpty(photoUrl) ? 'card-img-no' : 'card-img'}
            onError={handleImageError}
          />
        </Link>
      </div>
      <div className='gap-x-2 px-4 pt-4 truncate font-semibold'>
        <Link href={url}>
          {d.year} {d.make} {d.model} {d.trim}
        </Link>
      </div>
      <div className='flex items-center justify-between'>
        <Price v={d} />
        <div className='w-fit mt-3 mb-2 px-4 py-2 bg-zinc-100 dark:bg-zinc-800 shadow-inner rounded-l-full flex items-center gap-x-2 truncate'>
          <div className='text-sm font-normal truncate'>{d.condition_name}</div>
          <Mileage d={d?.mileage || 0} />
        </div>
      </div>
      <CardBody>
        {showPostStatus && <CardItem label='Post Status' alert={(d?.post_status_id || 0) < 1} value={getPostStatus()} />}
        {showSaleStatus && <CardItem label='Sale Status' alert={(d?.sale_status_id || 0) < 1} value={getSaleStatus()} />}
        <CardItem label='VIN' value={d?.vin || '-'} />
        <CardItem label='Drive Type' value={d?.drive_type_name || '-'} />
        <CardItem label='Engine' value={d?.engine?.name || '-'} />
        <CardItem label='Transmission' value={d?.transmission?.name || '-'} />
      </CardBody>
      <Location />
      {showSavedInfo ? <SavedInfo /> : <UserInfo />}
      <div className='flex items-center justify-between font-light px-4 py-2 border-t mt-auto'>
        <div className='flex items-center gap-x-2'>
          <div className='text-xs/4'>
            <span className='font-medium text-sm'>{d?.view_count?.toLocaleString()}</span> view
            {(d?.view_count || 0) > 1 && 's'}
          </div>
          <Separator />
          <div className='text-xs'>Listed {moment(d.date_create).fromNow()}</div>
        </div>
      </div>
    </>
  )
}

const stylePriceText = 'pl-4 text-sm font-semibold dark:font-normal'

const PriceDropIcon = ({ className }: { className: string }) => {
  return (
    <svg fill='currentColor' className={className} viewBox='-2 0 19 19' xmlns='http://www.w3.org/2000/svg'>
      <title>Price Reduced</title>
      <g id='SVGRepo_bgCarrier' strokeWidth='0'></g>
      <g id='SVGRepo_tracerCarrier' strokeLinecap='round' strokeLinejoin='round'></g>
      <g id='SVGRepo_iconCarrier'>
        <path d='m13.842 11.52-4.389 4.388a1.112 1.112 0 0 1-1.567 0l-6.28-6.28a3.027 3.027 0 0 1-.771-1.892l.043-3.681A1.141 1.141 0 0 1 2 2.935L5.67 2.9a3.04 3.04 0 0 1 1.892.773l6.28 6.28a1.112 1.112 0 0 1 0 1.567zM3.826 5.133a.792.792 0 1 0-.792.792.792.792 0 0 0 .792-.792zm6.594 7.348a.554.554 0 0 0 0-.784l-.401-.401a2.53 2.53 0 0 0 .35-.83 1.565 1.565 0 0 0-.397-1.503 1.59 1.59 0 0 0-1.017-.46 2.14 2.14 0 0 0-.75.085h-.002a2.444 2.444 0 0 0-.59.277H7.61a2.677 2.677 0 0 0-.438.357 2.043 2.043 0 0 1-.259.22 1.29 1.29 0 0 1-.329.17h-.002a.835.835 0 0 1-.338.038h-.002a.53.53 0 0 1-.314-.136.539.539 0 0 1-.106-.534 1.54 1.54 0 0 1 .41-.71 1.632 1.632 0 0 1 .23-.165l.03-.019a1.783 1.783 0 0 1 .322-.155.942.942 0 0 1 .325-.06.554.554 0 0 0 0-1.108h-.001a2.058 2.058 0 0 0-.717.132 2.846 2.846 0 0 0-.529.26l-.01.006-.398-.4a.554.554 0 1 0-.784.785l.388.387a2.513 2.513 0 0 0-.347.803 1.644 1.644 0 0 0 .404 1.561 1.622 1.622 0 0 0 .983.456 1.922 1.922 0 0 0 .805-.089 2.372 2.372 0 0 0 .624-.319 3.142 3.142 0 0 0 .398-.339 1.569 1.569 0 0 1 .256-.208 1.381 1.381 0 0 1 .32-.151 1.023 1.023 0 0 1 .348-.038.485.485 0 0 1 .308.139c.05.049.165.165.097.488a1.558 1.558 0 0 1-.413.729 2.476 2.476 0 0 1-.28.219 1.727 1.727 0 0 1-.306.157.687.687 0 0 1-.32.042.554.554 0 1 0-.08 1.106c.052.004.103.005.152.005a1.723 1.723 0 0 0 .685-.134 2.678 2.678 0 0 0 .507-.27l.01-.007.397.398a.555.555 0 0 0 .783 0z'></path>
      </g>
    </svg>
  )
}

const Price = ({ v }: { v: Vehicle.Summary }) => {
  const price = v?.price || 0
  if (v.sale_status_id === Vehicle.SaleStatusEnum.Sold) {
    return <div className={stylePriceText}>Sold</div>
  }
  if (price > 0) {
    return (
      <div className='pl-4 flex items-center antialiased'>
        <sup className='text-base md:text-sm pt-2.5 font-medium mr-0.5 subpixel-antialiased'>$</sup>
        <span className='font-price font-bold dark:font-semibold text-3xl md:text-2xl tracking-wide'>{price.toLocaleString()}</span>
        {v.show_price_dropped && (v?.history?.price_change?.value || 0) < 0 && <PriceDropIcon className='size-7 ml-1 text-red-700' />}
      </div>
    )
  }
  if (v?.user_id) {
    return <div className={stylePriceText}>{v?.sale_status?.name}</div>
  }
  return <div className={stylePriceText}>Price Unknown</div>
}

// mileage
const Mileage = ({ d }: { d: number }) => {
  if (!d || d === 0) {
    return null
  }
  return (
    <>
      <Separator />
      <div className='text-sm'>
        <span className='font-semibold'>{d.toLocaleString()}</span>
        <span className='font-light ml-1'>miles</span>
      </div>
    </>
  )
}
